import { LABELS } from '@mm/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import {
  Alert,
  Card,
  DetailList,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../../components/ui/display';
import type { PlayDeveloperAccount } from '../../types/models';
import { formatDate } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { VerificationBadge } from './configs';
import { resourceKey } from './MasterPage';

const mail = (email: string | null) =>
  email ? (
    <a href={`mailto:${email}`} className="text-brand-700 hover:underline">
      {email}
    </a>
  ) : null;

/** Google Play developer account: identity, verification, access and documents. */
export function PlayDeveloperAccountDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const { data: r, error } = useQuery({
    queryKey: [...resourceKey('play-developer-accounts'), 'detail', id],
    queryFn: () => api.get<PlayDeveloperAccount>(`/play-developer-accounts/${id}`),
    enabled: !!id,
  });
  if (!r) {
    return error ? (
      <Alert tone="error">{error instanceof ApiError ? error.message : 'Failed to load'}</Alert>
    ) : (
      <Spinner />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={
          <Link to="/play-developer-accounts" className="text-brand-700 hover:underline">
            Play developer accounts
          </Link>
        }
        title={r.name}
        description={
          [r.developerId, r.accountType && LABELS.playAccountType[r.accountType]]
            .filter(Boolean)
            .join(' · ') || undefined
        }
        actions={<StatusBadge status={r.status} />}
      />
      <Card title="Account">
        <DetailList
          items={[
            { label: 'Developer name', value: r.name },
            { label: 'Developer account ID', value: r.developerId },
            {
              label: 'Account type',
              value: r.accountType ? LABELS.playAccountType[r.accountType] : null,
            },
            { label: 'Opened on', value: r.openedOn ? formatDate(r.openedOn) : null },
            { label: 'Opened on device', value: r.openedOnDevice },
            { label: 'Login email', value: mail(r.loginEmail) },
            { label: 'Access email', value: mail(r.accessEmail) },
            { label: 'Notes', value: r.notes },
          ]}
        />
      </Card>
      <Card title="Play Console verification">
        <DetailList
          items={[
            { label: 'Status', value: <VerificationBadge status={r.verificationStatus} /> },
            { label: 'Verification mobile', value: r.verificationDevice },
            { label: 'Verification mobile number', value: r.verificationPhone },
            { label: 'Verified on', value: r.verifiedOn ? formatDate(r.verifiedOn) : null },
          ]}
        />
      </Card>
      <Card title="Access">
        <DetailList
          items={[
            { label: 'Access device', value: r.accessDevice },
            { label: 'Access device internet', value: r.accessInternet },
          ]}
        />
      </Card>
      <Card title="About the developer" description="As shown on the Google Play listing">
        <DetailList
          items={[
            { label: 'Legal name', value: r.legalName },
            { label: 'Developer email', value: mail(r.contactEmail) },
            { label: 'Phone', value: r.phone },
            {
              label: 'Website',
              value: r.website ? (
                <a
                  href={r.website}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all text-brand-700 hover:underline"
                >
                  {r.website}
                </a>
              ) : null,
            },
            {
              label: 'Address',
              value: r.address ? <span className="whitespace-pre-line">{r.address}</span> : null,
            },
          ]}
        />
      </Card>
      <Card
        title="Verification documents"
        description="ID proof, address proof, D-U-N-S / company papers. Visible to administrators only."
      >
        {can('masters.write') ? (
          <AttachmentsPanel
            entityType="PlayDeveloperAccount"
            entityId={r.id}
            title="Documents"
            writePermission="masters.write"
            bare
          />
        ) : (
          <p className="text-sm text-slate-500">Only administrators can see these documents.</p>
        )}
      </Card>
      <Card title="Apps" description="Apps published from this developer account">
        {r.apps?.length ? (
          <ul className="divide-y divide-slate-100">
            {r.apps.map((a) => (
              <li key={a.id} className="flex items-center gap-3 py-2">
                <AppIcon src={a.iconUrl} name={a.name} size={28} />
                <span className="min-w-0 flex-1">
                  <Link to={`/apps/${a.id}`} className="font-medium text-brand-700 hover:underline">
                    {a.name}
                  </Link>
                  {a.packageName && (
                    <span className="block font-mono text-xs text-slate-400">{a.packageName}</span>
                  )}
                </span>
                <StatusBadge status={a.status} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">
            No apps yet. Link an app from its page (Google Play developer account).
          </p>
        )}
      </Card>
    </div>
  );
}
